import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Inventory2 as Inventory2Icon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Print as PrintIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Lock as LockIcon,
  LockOutlined as LockOutlinedIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalMall as LocalMallIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosProgramaCompras,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import VistaCompra from './VistaCompra';
import DocumentoEntradaRecepcion from './DocumentoEntradaRecepcion';

/** Clave del programa para validar permisos sobre Recepción de Compras. */
export const PROGRAMA_RECEPCION_COMPRAS = 'recepcioncompras';

const ESTADOS_FILTRO = [
  { value: '', label: 'Todos' },
  { value: 'A', label: 'Abiertas' },
  { value: 'C', label: 'Cerradas' },
];

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const chipEstadoRecepcionSx = (nombre) => {
  const n = (nombre || '').toLowerCase();
  if (n.includes('cerrada')) {
    return { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary };
  }
  if (n.includes('recibida')) {
    return { bgcolor: FP.color.successSoft || '#E8F5E9', color: FP.color.success || '#2E7D32' };
  }
  if (n.includes('parcial')) {
    return { bgcolor: FP.color.warningSoft || '#FFF8E1', color: FP.color.warning || '#F57C00' };
  }
  return { bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue };
};

const stickyHeadProps = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  '& td': {
    border: 'none',
    borderBottom: `2px solid ${FP.color.border}`,
    bgcolor: FP.color.surfaceAlt,
  },
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

const btnPrimarySx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: FP.color.surfaceAlt, color: FP.color.textMuted },
};

const btnOutlinedSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
};

const RecepcionCompra = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const permisoLeer = useMemo(
    () => tienePermisosProgramaCompras(roleDetalles, PROGRAMA_RECEPCION_COMPRAS),
    [roleDetalles]
  );
  const permisoModificar = useMemo(
    () =>
      puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_RECEPCION_COMPRAS) ||
      tienePermisosProgramaCompras(roleDetalles, PROGRAMA_RECEPCION_COMPRAS),
    [roleDetalles]
  );

  const [ordenes, setOrdenes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [expandidoId, setExpandidoId] = useState(null);
  const [localidades, setLocalidades] = useState([]);
  const [localidadPorLinea, setLocalidadPorLinea] = useState({});
  const [cantidadRecibir, setCantidadRecibir] = useState({});
  const [precioRecepcion, setPrecioRecepcion] = useState({});
  const [borradorPorOrden, setBorradorPorOrden] = useState({});
  const [guardandoOrdenId, setGuardandoOrdenId] = useState(null);
  const [cerrandoId, setCerrandoId] = useState(null);
  const [compraParaImprimir, setCompraParaImprimir] = useState(null);
  const [recepcionParaImprimir, setRecepcionParaImprimir] = useState(null);

  const credenciales = () => ({
    id_empresa: selectedCompany,
    usuario: usuarioLog || '',
    password: claveLog || '',
  });

  const keyLinea = (ordenId, item) => `${ordenId}-${item.id_linea ?? item.linea_idx}`;

  const cargarLocalidades = async () => {
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerlocalidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credenciales()),
      });
      if (response.ok) {
        const data = await response.json();
        setLocalidades(data.localidades || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchOrdenes = async () => {
    const body = {
      ...credenciales(),
      anomesfacturacion: selectedCompany?.anomesfacturacion,
    };
    if (filtroStatus) body.status = filtroStatus;
    if (fechaDesde) body.fecha_desde = fechaDesde;
    if (fechaHasta) body.fecha_hasta = fechaHasta;
    const response = await fetch(servidor + '/api/comprasbackend_listarcompras', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (response.ok) {
      const data = await response.json();
      setOrdenes(data.ordenes || []);
    } else {
      const err = await response.json();
      showError(err.Error || 'Error al listar órdenes de compra');
    }
  };

  const cargarDatos = async () => {
    if (!permisoLeer || !selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      await Promise.all([fetchOrdenes(), cargarLocalidades()]);
    } catch (e) {
      showError('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  };

  const listarOrdenes = async () => {
    if (!permisoLeer || !selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      await fetchOrdenes();
    } catch (e) {
      showError('Error de conexión');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (permisoLeer) cargarDatos();
  }, [permisoLeer, selectedCompany]);

  const toggleExpandir = (id) => {
    setExpandidoId((prev) => (prev === id ? null : id));
  };

  const setLocalidadLinea = (k, valor) => {
    setLocalidadPorLinea((prev) => ({ ...prev, [k]: valor }));
  };

  const setCantLinea = (k, valor) => {
    setCantidadRecibir((prev) => ({ ...prev, [k]: valor }));
  };

  const setPrecioLinea = (k, valor) => {
    setPrecioRecepcion((prev) => ({ ...prev, [k]: valor }));
  };

  const precioSugeridoLinea = (item) => {
    const pr = item.preciorecibido;
    if (pr != null && pr !== '') return Number(pr);
    return Number(item.precio || 0);
  };

  const nuevaIdBorrador = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `b-${Date.now()}-${Math.random()}`;

  const parseValidacionLinea = (orden, item) => {
    const k = keyLinea(orden.id, item);
    const loc = localidadPorLinea[k] || '';
    const cantRaw = cantidadRecibir[k];
    const cant = parseFloat(String(cantRaw ?? '').replace(',', '.'));
    if (!loc) {
      return { ok: false, error: 'Seleccione el almacén' };
    }
    if (Number.isNaN(cant) || cant <= 0) {
      return { ok: false, error: 'Indique una cantidad válida a recibir' };
    }
    const precioRaw = precioRecepcion[k];
    let precioRec = null;
    if (precioRaw !== undefined && precioRaw !== null && String(precioRaw).trim() !== '') {
      precioRec = parseFloat(String(precioRaw).replace(',', '.'));
      if (Number.isNaN(precioRec) || precioRec < 0) {
        return {
          ok: false,
          error: 'Indique un precio de recepción válido o déjelo vacío para usar el precio de la orden',
        };
      }
    }
    return { ok: true, k, loc, cant, precioRec };
  };

  const agregarLineaBorrador = (orden, item) => {
    if (!permisoModificar) {
      showError('No tiene permiso para registrar la recepción de compras');
      return;
    }
    const pend = item.pendiente ?? 0;
    const puedeRecibir = permisoModificar && orden.status !== 'C' && pend > 0;
    if (!puedeRecibir) return;

    const parsed = parseValidacionLinea(orden, item);
    if (!parsed.ok) {
      showError(parsed.error);
      return;
    }
    const { k, loc, cant, precioRec } = parsed;
    const borrador = borradorPorOrden[orden.id] || [];
    const yaEnBorrador = borrador.filter((r) => r.keyLinea === k).reduce((s, r) => s + r.cantidad, 0);
    if (yaEnBorrador + cant > pend + 0.0001) {
      showError(`La cantidad en borrador (${yaEnBorrador}) más esta línea (${cant}) supera lo pendiente (${pend})`);
      return;
    }

    setBorradorPorOrden((prev) => ({
      ...prev,
      [orden.id]: [
        ...(prev[orden.id] || []),
        {
          id: nuevaIdBorrador(),
          keyLinea: k,
          item: { ...item },
          cantidad: cant,
          localidad_id: loc,
          precio_recepcion: precioRec,
        },
      ],
    }));
    setCantidadRecibir((p) => ({ ...p, [k]: '' }));
    setPrecioRecepcion((p) => {
      const next = { ...p };
      delete next[k];
      return next;
    });
  };

  const quitarLineaBorrador = (ordenId, rowId) => {
    setBorradorPorOrden((prev) => ({
      ...prev,
      [ordenId]: (prev[ordenId] || []).filter((r) => r.id !== rowId),
    }));
  };

  const ejecutarRecepcionLinea = async (orden, item, cant, loc, precioRec) => {
    const body = {
      ...credenciales(),
      anomesfacturacion: selectedCompany?.anomesfacturacion,
      cantidad: cant,
      localidad_id: loc,
    };
    if (precioRec !== null && precioRec !== undefined) body.precio_recepcion = precioRec;
    if (item.id_linea != null) body.id_linea = item.id_linea;
    else {
      body.compra_id = orden.id;
      body.linea_idx = item.linea_idx;
    }
    const response = await fetch(servidor + '/api/comprasbackend_recibirlinea', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.Error || 'Error al registrar recepción');
    }
    return response.json();
  };

  const guardarBorradorOrden = async (orden) => {
    if (!permisoModificar) {
      showError('No tiene permiso para registrar la recepción de compras');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const filas = borradorPorOrden[orden.id] || [];
    if (filas.length === 0) {
      showError('Agregue al menos una línea al borrador antes de guardar');
      return;
    }
    setGuardandoOrdenId(orden.id);
    try {
      for (const fila of filas) {
        await ejecutarRecepcionLinea(
          orden,
          fila.item,
          fila.cantidad,
          fila.localidad_id,
          fila.precio_recepcion
        );
      }
      setBorradorPorOrden((prev) => ({ ...prev, [orden.id]: [] }));
      showSuccess('Recepción guardada e inventario actualizado');
      await listarOrdenes();
      try {
        await abrirDetalleVista(orden.id, true);
      } catch (e2) {
        /* detalle ya notificó error; datos guardados */
      }
    } catch (e) {
      showError(e.message || 'Error al guardar la recepción');
    } finally {
      setGuardandoOrdenId(null);
    }
  };

  const cerrarOrden = async (orden) => {
    if (!permisoModificar) {
      showError('No tiene permiso para registrar la recepción de compras');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!window.confirm(`¿Cerrar la orden de compra Nº ${orden.id}? No podrá recibir más mercancía en esta orden.`))
      return;
    setCerrandoId(orden.id);
    try {
      const response = await fetch(servidor + '/api/comprasbackend_cerrarordencompra', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          compra_id: orden.id,
        }),
      });
      if (response.ok) {
        showSuccess('Orden cerrada');
        listarOrdenes();
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al cerrar la orden');
      }
    } catch (e) {
      showError('Error de conexión');
    } finally {
      setCerrandoId(null);
    }
  };

  const abrirDetalleVista = async (compraId, soloRecepcion) => {
    if (!permisoLeer || !selectedCompany?.db_empresa) return;
    let response;
    try {
      response = await fetch(servidor + '/api/comprasbackend_detallecompra', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          anomesfacturacion: selectedCompany?.anomesfacturacion,
          compra_id: compraId,
          solo_recepcion: soloRecepcion,
        }),
      });
    } catch (e) {
      showError('Error de conexión');
      throw e;
    }
    if (response.ok) {
      const data = await response.json();
      const det = data.detalle || {};
      const payload = {
        ...det,
        id_empresa: selectedCompany,
        empresa: selectedCompany?.empresa || selectedCompany?.nombre || selectedCompany,
      };
      if (soloRecepcion) {
        setRecepcionParaImprimir(payload);
        setCompraParaImprimir(null);
      } else {
        setCompraParaImprimir(payload);
        setRecepcionParaImprimir(null);
      }
      return;
    }
    const err = await response.json().catch(() => ({}));
    const msg = err.Error || 'Error al cargar el documento';
    showError(msg);
    throw new Error(msg);
  };

  if (!permisoLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Recepción de compras"
          subtitle={nombreNegocio ? `${nombreNegocio} · Entrada de mercancía` : 'Entrada de mercancía'}
        />
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              ...cardShellSx,
              maxWidth: 520,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: FP.color.textPrimary, mb: 1 }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «recepcioncompras» (o umbrella «mantenimientocompras») en roles.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Recepción de compras"
        subtitle={nombreNegocio ? `${nombreNegocio} · Entrada de mercancía` : 'Entrada de mercancía'}
        cta={
          <Button
            startIcon={<RefreshIcon />}
            onClick={listarOrdenes}
            disabled={cargando || !selectedCompany?.db_empresa}
            variant="outlined"
            sx={btnOutlinedSx}
          >
            {cargando ? 'Cargando…' : 'Actualizar'}
          </Button>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 2.5 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Inventory2Icon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel shrink>Estado orden</InputLabel>
                  <Select
                    value={filtroStatus}
                    label="Estado orden"
                    onChange={(e) => setFiltroStatus(e.target.value)}
                  >
                    {ESTADOS_FILTRO.map((e) => (
                      <MenuItem key={e.value || 'all'} value={e.value}>
                        {e.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<FilterListIcon />}
                  onClick={listarOrdenes}
                  disabled={cargando || !selectedCompany?.db_empresa}
                  sx={{ ...btnPrimarySx, height: 46 }}
                >
                  Filtrar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalMallIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Órdenes de compra
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={listarOrdenes}
              disabled={cargando || !selectedCompany?.db_empresa}
              size="small"
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              {cargando ? 'Cargando…' : 'Actualizar'}
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                overflow: 'auto',
                maxHeight: '60vh',
              }}
            >
              <Table size="small" stickyHeader>
                <TableBody>
                  <TableRow sx={stickyHeadProps}>
                    <TableCell sx={{ ...cellHeadSx, width: 48 }} />
                    <TableCell sx={cellHeadSx}>Nº orden</TableCell>
                    <TableCell sx={cellHeadSx}>Fecha</TableCell>
                    <TableCell sx={cellHeadSx}>Suplidor</TableCell>
                    <TableCell sx={cellHeadSx}>Estado OC</TableCell>
                    <TableCell sx={cellHeadSx}>Recepción</TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Reimprimir OC
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Doc. recepción
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                  {ordenes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}>
                          {cargando
                            ? 'Cargando…'
                            : 'No hay órdenes de compra con los filtros aplicados.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ordenes.map((o) => (
                      <React.Fragment key={o.id}>
                        <TableRow
                          hover
                          sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
                          onClick={() => toggleExpandir(o.id)}
                        >
                          <TableCell sx={{ width: 48 }}>
                            <IconButton
                              size="small"
                              aria-label={expandidoId === o.id ? 'Ocultar ítems' : 'Ver ítems'}
                              sx={{ color: FP.color.accentBlue }}
                            >
                              {expandidoId === o.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={o.id}
                              size="small"
                              sx={{
                                bgcolor: FP.color.accentBlueSoft,
                                color: FP.color.accentBlue,
                                fontWeight: 700,
                                fontSize: '0.74rem',
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              color: FP.color.textPrimary,
                              fontSize: '0.86rem',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {o.fecha_str || '—'}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              color: FP.color.textPrimary,
                              fontSize: '0.86rem',
                            }}
                            title={o.suplidor_nombre || o.suplidor || ''}
                          >
                            {o.suplidor_nombre || o.suplidor || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={o.status === 'C' ? 'Cerrada' : 'Abierta'}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.74rem',
                                ...(o.status === 'C'
                                  ? {
                                      bgcolor: FP.color.surfaceAlt,
                                      color: FP.color.textSecondary,
                                    }
                                  : {
                                      bgcolor: FP.color.accentBlueSoft,
                                      color: FP.color.accentBlue,
                                    }),
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={o.estado_recepcion || '—'}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.74rem',
                                ...chipEstadoRecepcionSx(o.estado_recepcion),
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={() => abrirDetalleVista(o.id, false)}
                              title="Reimprimir orden de compra"
                              sx={{
                                color: FP.color.accentBlue,
                                '&:hover': { bgcolor: FP.color.accentBlueSoft },
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              onClick={() => abrirDetalleVista(o.id, true)}
                              title="Generar documento de recepción"
                              disabled={!o.items?.some((it) => parseFloat(it.recibido) > 0)}
                              sx={{
                                color: FP.color.navy,
                                '&:hover': { bgcolor: FP.color.surfaceAlt },
                              }}
                            >
                              <AssignmentTurnedInIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            {permisoModificar && o.status !== 'C' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<LockIcon />}
                                onClick={() => cerrarOrden(o)}
                                disabled={cerrandoId === o.id}
                                sx={{
                                  borderColor: FP.color.border,
                                  color: FP.color.textPrimary,
                                  bgcolor: '#FFFFFF',
                                  borderRadius: 1.2,
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  '&:hover': {
                                    bgcolor: FP.color.surfaceAlt,
                                    borderColor: FP.color.borderStrong,
                                  },
                                }}
                              >
                                {cerrandoId === o.id ? '…' : 'Cerrar'}
                              </Button>
                            ) : (
                              <Typography sx={{ color: FP.color.textMuted, fontSize: '0.86rem' }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, borderBottom: expandidoId === o.id ? undefined : 0 }}>
                            <Collapse in={expandidoId === o.id} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: { xs: 1.5, sm: 2 }, bgcolor: FP.color.surface }}>
                                <Card elevation={0} sx={{ ...cardShellSx, mb: 2.5 }}>
                                  <Box sx={filterHeaderSx}>
                                    <Box
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: 1.2,
                                        bgcolor: FP.color.accentBlueSoft,
                                        color: FP.color.accentBlue,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <ShoppingCartIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}
                                      >
                                        Items de la factura
                                      </Typography>
                                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                                        Complete almacén, cantidad y precio; pulse «Agregar» para pasar al borrador
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <TableContainer
                                      component={Paper}
                                      elevation={0}
                                      sx={{
                                        border: `1px solid ${FP.color.border}`,
                                        borderRadius: 2,
                                        overflow: 'auto',
                                        maxHeight: 360,
                                      }}
                                    >
                                      <Table size="small" stickyHeader>
                                        <TableBody>
                                          <TableRow sx={stickyHeadProps}>
                                            <TableCell sx={cellHeadSx}>Código</TableCell>
                                            <TableCell sx={cellHeadSx}>Descripción</TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'right' }}
                                              align="right"
                                            >
                                              Pedido
                                            </TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'right' }}
                                              align="right"
                                            >
                                              Recibido
                                            </TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'right' }}
                                              align="right"
                                            >
                                              Pendiente
                                            </TableCell>
                                            <TableCell sx={{ ...cellHeadSx, minWidth: 180 }}>
                                              Almacén
                                            </TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'center', minWidth: 108 }}
                                              align="center"
                                            >
                                              Cant.
                                            </TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'right', minWidth: 120 }}
                                              align="right"
                                            >
                                              Precio
                                            </TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'center' }}
                                              align="center"
                                            >
                                              Acción
                                            </TableCell>
                                          </TableRow>
                                          {(o.items || []).length === 0 ? (
                                            <TableRow>
                                              <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                                                <Typography
                                                  sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}
                                                >
                                                  No hay items en la factura.
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          ) : (
                                            (o.items || []).map((item) => {
                                              const k = keyLinea(o.id, item);
                                              const pend = item.pendiente ?? 0;
                                              const puedeRecibir =
                                                permisoModificar && o.status !== 'C' && pend > 0;
                                              const sugerido = precioSugeridoLinea(item);
                                              return (
                                                <TableRow key={k} hover>
                                                  <TableCell>
                                                    <Chip
                                                      label={item.item || '—'}
                                                      size="small"
                                                      sx={{
                                                        bgcolor: FP.color.accentBlueSoft,
                                                        color: FP.color.accentBlue,
                                                        fontWeight: 700,
                                                        fontSize: '0.74rem',
                                                      }}
                                                    />
                                                  </TableCell>
                                                  <TableCell
                                                    sx={{
                                                      fontWeight: 500,
                                                      color: FP.color.textPrimary,
                                                      fontSize: '0.86rem',
                                                    }}
                                                    title={item.descripcion || ''}
                                                  >
                                                    {item.descripcion || '—'}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{
                                                      fontVariantNumeric: 'tabular-nums',
                                                      fontSize: '0.86rem',
                                                      color: FP.color.textPrimary,
                                                    }}
                                                  >
                                                    {Number(item.cantidad)}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{
                                                      fontVariantNumeric: 'tabular-nums',
                                                      fontSize: '0.86rem',
                                                      color: FP.color.textPrimary,
                                                    }}
                                                  >
                                                    {Number(item.recibido || 0)}
                                                  </TableCell>
                                                  <TableCell
                                                    align="right"
                                                    sx={{
                                                      fontWeight: 700,
                                                      fontVariantNumeric: 'tabular-nums',
                                                      fontSize: '0.86rem',
                                                      color: FP.color.navy,
                                                    }}
                                                  >
                                                    {pend}
                                                  </TableCell>
                                                  <TableCell>
                                                    <FormControl
                                                      size="small"
                                                      fullWidth
                                                      disabled={!puedeRecibir}
                                                      sx={fpInputSx}
                                                    >
                                                      <InputLabel shrink>Almacén</InputLabel>
                                                      <Select
                                                        label="Almacén"
                                                        value={localidadPorLinea[k] || ''}
                                                        onChange={(e) =>
                                                          setLocalidadLinea(k, e.target.value)
                                                        }
                                                      >
                                                        <MenuItem value="">
                                                          <em>Seleccione</em>
                                                        </MenuItem>
                                                        {localidades.map((loc) => (
                                                          <MenuItem
                                                            key={loc.id}
                                                            value={loc.texto || loc.id}
                                                          >
                                                            {loc.texto || loc.id}
                                                          </MenuItem>
                                                        ))}
                                                      </Select>
                                                    </FormControl>
                                                  </TableCell>
                                                  <TableCell align="center">
                                                    <TextField
                                                      size="small"
                                                      type="number"
                                                      inputProps={{
                                                        min: 0,
                                                        step: 0.01,
                                                        style: { textAlign: 'center' },
                                                      }}
                                                      value={cantidadRecibir[k] ?? ''}
                                                      onChange={(e) => setCantLinea(k, e.target.value)}
                                                      disabled={!puedeRecibir}
                                                      sx={{ width: 90, ...fpInputSx }}
                                                    />
                                                  </TableCell>
                                                  <TableCell align="right">
                                                    <TextField
                                                      size="small"
                                                      type="number"
                                                      inputProps={{
                                                        min: 0,
                                                        step: 0.01,
                                                        style: { textAlign: 'right' },
                                                      }}
                                                      value={precioRecepcion[k] ?? ''}
                                                      onChange={(e) => setPrecioLinea(k, e.target.value)}
                                                      disabled={!puedeRecibir}
                                                      placeholder={fmtMoney(sugerido)}
                                                      title="Precio unitario de esta recepción. Vacío = precio de la orden de compra."
                                                      sx={{ width: 110, ...fpInputSx }}
                                                    />
                                                  </TableCell>
                                                  <TableCell align="center">
                                                    <Button
                                                      size="small"
                                                      variant="contained"
                                                      disabled={!puedeRecibir}
                                                      onClick={() => agregarLineaBorrador(o, item)}
                                                      sx={{
                                                        bgcolor: FP.color.navy,
                                                        color: '#FFFFFF',
                                                        borderRadius: 1.2,
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                          bgcolor: FP.color.navyDeep,
                                                          boxShadow: 'none',
                                                        },
                                                        '&.Mui-disabled': {
                                                          bgcolor: FP.color.surfaceAlt,
                                                          color: FP.color.textMuted,
                                                        },
                                                      }}
                                                    >
                                                      Agregar
                                                    </Button>
                                                  </TableCell>
                                                </TableRow>
                                              );
                                            })
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </CardContent>
                                </Card>

                                <Card elevation={0} sx={cardShellSx}>
                                  <Box sx={filterHeaderSx}>
                                    <Box
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        borderRadius: 1.2,
                                        bgcolor: FP.color.accentBlueSoft,
                                        color: FP.color.accentBlue,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <AssignmentTurnedInIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}
                                      >
                                        Borrador de recepción — orden Nº {o.id}
                                      </Typography>
                                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                                        Al guardar se actualiza el inventario y se abre el documento de entrada
                                      </Typography>
                                    </Box>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<SaveIcon />}
                                      disabled={
                                        !permisoModificar ||
                                        o.status === 'C' ||
                                        (borradorPorOrden[o.id] || []).length === 0 ||
                                        guardandoOrdenId === o.id
                                      }
                                      onClick={() => guardarBorradorOrden(o)}
                                      sx={{
                                        bgcolor: FP.color.navy,
                                        color: '#FFFFFF',
                                        borderRadius: 1.4,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        boxShadow: 'none',
                                        flexShrink: 0,
                                        '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                                        '&.Mui-disabled': {
                                          bgcolor: FP.color.surfaceAlt,
                                          color: FP.color.textMuted,
                                        },
                                      }}
                                    >
                                      {guardandoOrdenId === o.id ? 'Guardando…' : 'Guardar recepción'}
                                    </Button>
                                  </Box>
                                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <TableContainer
                                      component={Paper}
                                      elevation={0}
                                      sx={{
                                        border: `1px solid ${FP.color.border}`,
                                        borderRadius: 2,
                                        overflow: 'auto',
                                        maxHeight: 320,
                                      }}
                                    >
                                      <Table size="small" stickyHeader>
                                        <TableBody>
                                          <TableRow sx={stickyHeadProps}>
                                            <TableCell sx={cellHeadSx}>Código</TableCell>
                                            <TableCell sx={cellHeadSx}>Descripción</TableCell>
                                            <TableCell sx={cellHeadSx}>Almacén</TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'center' }}
                                              align="center"
                                            >
                                              Cant.
                                            </TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'right' }}
                                              align="right"
                                            >
                                              Precio
                                            </TableCell>
                                            <TableCell
                                              sx={{ ...cellHeadSx, textAlign: 'center' }}
                                              align="center"
                                            >
                                              Acción
                                            </TableCell>
                                          </TableRow>
                                          {(borradorPorOrden[o.id] || []).length === 0 ? (
                                            <TableRow>
                                              <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                                <Typography
                                                  sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}
                                                >
                                                  No hay items en el borrador. Use «Agregar» desde la tabla superior.
                                                </Typography>
                                              </TableCell>
                                            </TableRow>
                                          ) : (
                                            (borradorPorOrden[o.id] || []).map((fila) => (
                                              <TableRow key={fila.id} hover>
                                                <TableCell>
                                                  <Chip
                                                    label={fila.item.item || '—'}
                                                    size="small"
                                                    sx={{
                                                      bgcolor: FP.color.accentBlueSoft,
                                                      color: FP.color.accentBlue,
                                                      fontWeight: 700,
                                                      fontSize: '0.74rem',
                                                    }}
                                                  />
                                                </TableCell>
                                                <TableCell
                                                  sx={{
                                                    fontWeight: 500,
                                                    color: FP.color.textPrimary,
                                                    fontSize: '0.86rem',
                                                  }}
                                                  title={fila.item.descripcion || ''}
                                                >
                                                  {fila.item.descripcion || '—'}
                                                </TableCell>
                                                <TableCell
                                                  sx={{
                                                    color: FP.color.textPrimary,
                                                    fontSize: '0.86rem',
                                                  }}
                                                >
                                                  {fila.localidad_id}
                                                </TableCell>
                                                <TableCell
                                                  align="center"
                                                  sx={{
                                                    fontWeight: 700,
                                                    fontVariantNumeric: 'tabular-nums',
                                                    fontSize: '0.86rem',
                                                    color: FP.color.textPrimary,
                                                  }}
                                                >
                                                  {fila.cantidad}
                                                </TableCell>
                                                <TableCell
                                                  align="right"
                                                  sx={{
                                                    fontWeight: 700,
                                                    fontVariantNumeric: 'tabular-nums',
                                                    fontSize: '0.86rem',
                                                    color: FP.color.textPrimary,
                                                  }}
                                                >
                                                  {fila.precio_recepcion != null
                                                    ? `$${fmtMoney(fila.precio_recepcion)}`
                                                    : '(precio OC)'}
                                                </TableCell>
                                                <TableCell align="center">
                                                  <IconButton
                                                    size="small"
                                                    aria-label="Quitar línea del borrador"
                                                    onClick={() => quitarLineaBorrador(o.id, fila.id)}
                                                    disabled={guardandoOrdenId === o.id}
                                                    sx={{
                                                      color: FP.color.danger,
                                                      '&:hover': { bgcolor: FP.color.dangerSoft },
                                                    }}
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </TableCell>
                                              </TableRow>
                                            ))
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </CardContent>
                                </Card>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
              }}
            >
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.86rem', lineHeight: 1.55 }}>
                Expanda una orden para ver los items. Agregue líneas al borrador y pulse «Guardar recepción»
                para actualizar el inventario y abrir el documento de entrada. El precio vacío usa el de la
                orden. Use «Cerrar» para finalizar la OC.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <VistaCompra
        open={!!compraParaImprimir}
        onClose={() => setCompraParaImprimir(null)}
        compraData={compraParaImprimir || {}}
      />

      <DocumentoEntradaRecepcion
        open={!!recepcionParaImprimir}
        onClose={() => setRecepcionParaImprimir(null)}
        datos={recepcionParaImprimir || {}}
      />
    </Box>
  );
};

export default RecepcionCompra;
